import { useState, useCallback } from 'react';
import type { Dashboard, DashboardLayout, Panel, WidgetType } from '@/types/dashboard';
import { saveConfig } from '@/utils/api';
import { normalizeDashboard, cleanDashboardForSave } from '@/utils/persistence';
import {
  createPanel,
  addPanel,
  removePanel,
  updatePanel as updatePanelUtil,
  updatePanelData as updatePanelDataUtil,
  syncPanelPositions as syncPanelPositionsUtil,
} from '@/utils/panelManagement';

export interface UseDashboardReturn {
  dashboard: Dashboard | null;
  isDirty: boolean;
  isLoading: boolean;
  initialize: (config: Partial<Dashboard>) => void;
  save: () => Promise<void>;
  addPanel: (type: WidgetType, onAdded?: (newPanelId: string) => void) => void;
  removePanel: (id: string) => void;
  updatePanel: (id: string, updates: Partial<Panel>) => void;
  updatePanelData: (id: string, data: Panel['data']) => void;
  syncPanelPositions: (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => void;
  markPanelLoaded: (id: string, updates: Partial<Panel>) => void;
  updateLayout: (updates: Partial<DashboardLayout>) => void;
  updateTitle: (title: string) => void;
  resetDirty: () => void;
  resetAllPanelData: () => void;
  reinitialize: (config: Partial<Dashboard>) => void;
}

export function useDashboard(): UseDashboardReturn {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const initialize = useCallback((config: Partial<Dashboard>) => {
    const normalized = normalizeDashboard(config);
    setDashboard(normalized);
    setIsDirty(false);
  }, []);

  const handleAddPanel = useCallback((type: WidgetType, onAdded?: (newPanelId: string) => void) => {
    const newPanel = createPanel(type);
    setDashboard(prev => {
      if (!prev) return prev;
      const updatedPanels = addPanel(prev.panels, newPanel);
      return { ...prev, panels: updatedPanels };
    });
    if (onAdded) onAdded(newPanel.id);
    setIsDirty(true);
  }, []);

  const handleRemovePanel = useCallback((id: string) => {
    setDashboard(prev => {
      if (!prev) return prev;
      const updatedPanels = removePanel(prev.panels, id);
      return { ...prev, panels: updatedPanels };
    });
    setIsDirty(true);
  }, []);

  const handleUpdatePanel = useCallback((id: string, updates: Partial<Panel>) => {
    setDashboard(prev => {
      if (!prev) return prev;
      const updatedPanels = updatePanelUtil(prev.panels, id, updates);
      return { ...prev, panels: updatedPanels };
    });
    setIsDirty(true);
  }, []);

  const handleUpdatePanelData = useCallback((id: string, data: Panel['data']) => {
    setDashboard(prev => {
      if (!prev) return prev;
      const updatedPanels = updatePanelDataUtil(prev.panels, id, data);
      return { ...prev, panels: updatedPanels };
    });
    setIsDirty(true);
  }, []);

  const handleSyncPanelPositions = useCallback((layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
    setDashboard(prev => {
      if (!prev) return prev;
      const updatedPanels = syncPanelPositionsUtil(prev.panels, layout);
      return { ...prev, panels: updatedPanels };
    });
    setIsDirty(true);
  }, []);

  const markPanelLoaded = useCallback((id: string, updates: Partial<Panel>) => {
    setDashboard(prev => {
      if (!prev) return prev;
      const updatedPanels = updatePanelUtil(prev.panels, id, updates);
      return { ...prev, panels: updatedPanels };
    });
  }, []);

  const handleUpdateLayout = useCallback((updates: Partial<DashboardLayout>) => {
    setDashboard(prev => {
      if (!prev) return prev;
      return { ...prev, layout: { ...prev.layout, ...updates } };
    });
    setIsDirty(false);
  }, []);

  const handleUpdateTitle = useCallback((title: string) => {
    setDashboard(prev => {
      if (!prev) return prev;
      return { ...prev, title };
    });
  }, []);

  const resetDirty = useCallback(() => setIsDirty(false), []);

  const resetAllPanelData = useCallback(() => {
    setDashboard(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        panels: prev.panels.map(p => ({
          ...p,
          _dataLoaded: false,
          _isLoading: false,
          _loadError: null,
        })),
      };
    });
  }, []);

  const reinitialize = useCallback((config: Partial<Dashboard>) => {
    const normalized = normalizeDashboard(config);
    setDashboard({
      ...normalized,
      panels: normalized.panels.map(p => ({
        ...p,
        _dataLoaded: false,
        _isLoading: false,
        _loadError: null,
      })),
    });
    setIsDirty(false);
  }, []);

  const save = useCallback(async () => {
    if (!dashboard) return;
    const cleaned = cleanDashboardForSave(dashboard);
    await saveConfig(cleaned as Dashboard);
    setIsDirty(false);
  }, [dashboard]);

  return {
    dashboard,
    isDirty,
    isLoading: false,
    initialize,
    save,
    addPanel: handleAddPanel,
    removePanel: handleRemovePanel,
    updatePanel: handleUpdatePanel,
    updatePanelData: handleUpdatePanelData,
    syncPanelPositions: handleSyncPanelPositions,
    markPanelLoaded,
    updateLayout: handleUpdateLayout,
    updateTitle: handleUpdateTitle,
    resetDirty,
    resetAllPanelData,
    reinitialize,
  };
}
